import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import DashNavbar from "../components/PlumPlusDash/DashNavbar";
import { motion } from 'framer-motion';
import { Popover } from '@headlessui/react';
import { getGuideProgress, setGuideProgress } from "../utils/guideProgressUtils";

// -----------------------------------------------------------------------------
// 1. CustomTooltip Component (Popover)
// -----------------------------------------------------------------------------
const CustomTooltip = ({ children, content }) => (
  <Popover className="relative inline-block">
    <Popover.Button as="div">
      {children}
    </Popover.Button>

    <Popover.Panel className="absolute z-10 px-4 py-2 text-sm bg-gray-900 text-white rounded-md shadow-lg bottom-full mb-2 transform -translate-x-1/2 left-1/2">
      {content}
      <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2" />
    </Popover.Panel>
  </Popover>
);

// -----------------------------------------------------------------------------
// 2. Step Component (Accordion)
// -----------------------------------------------------------------------------
const Step = ({ id, title, children, isCompleted, onComplete }) => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useTranslation(['separation', 'common']);

  // Ensure children is rendered as text if it's an object
  const renderContent = (content) => {
    if (!content) return null;
    
    if (typeof content === 'object') {
      if (content.title && content.content) {
        return (
          <>
            <h4 className="font-semibold mb-2">{content.title}</h4>
            <p className="text-gray-600">{content.content}</p>
          </>
        );
      }
      return <pre className="text-sm">{JSON.stringify(content, null, 2)}</pre>;
    }
    
    return <p className="text-gray-600">{content}</p>;
  };

  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-white rounded-xl border ${
        isCompleted ? "border-green-200 bg-green-50/50" : "border-slate-200"
      } shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md`}
    >
      <div className={`p-4 ${isCompleted ? "bg-green-100" : "bg-blue-100"} ${
        isCompleted ? "text-green-700" : "text-blue-700"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isCompleted ? "bg-green-500" : "bg-blue-500"
              } text-white`}
            >
              {isCompleted ? "✓" : id.replace('step', '')}
            </div>
            <h3 className="text-lg font-bold">
              {title}
            </h3>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="focus:outline-none p-2 hover:bg-black/5 rounded-full transition-colors"
            aria-label={open ? t('common:common.closeSection') : t('common:common.openSection')}
          >
            <motion.span
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="block"
            >
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </motion.span>
          </button>
        </div>
      </div>

      <motion.div
        initial={false}
        animate={{ 
          height: open ? "auto" : 0,
          opacity: open ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="p-6">
          <div className="prose prose-lg max-w-none text-gray-600">
            {renderContent(children)}
          </div>

          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                showDetails 
                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {showDetails ? (
                <>
                  <span>{t('common:common.seeLess')}</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              ) : (
                <>
                  <span>{t('common:common.learnMore')}</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
            
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                isCompleted 
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {isCompleted ? (
                <>
                  <span>{t('common:common.completed')}</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              ) : (
                <>
                  <span>{t('common:common.markAsCompleted')}</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </>
              )}
            </button>
          </div>

          <motion.div
            initial={false}
            animate={{ 
              height: showDetails ? "auto" : 0,
              opacity: showDetails ? 1 : 0
            }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <div className="bg-gray-50 p-6 rounded-xl prose prose-lg max-w-none">
              {getDetailedContent(id)}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// -----------------------------------------------------------------------------
// 3. getDetailedContent: Detailed content for each step
// -----------------------------------------------------------------------------
const getDetailedContent = (stepId) => {
  const { t } = useTranslation(['separation', 'common']);
  const details = t(`separation:separationGuide.steps.${stepId}.details`, { returnObjects: true });

  if (!details) return null;

  const renderList = (items) => {
    if (!Array.isArray(items)) return null;
    return (
      <ul className="list-disc list-inside space-y-1">
        {items.map((item, index) => (
          <li key={index}>
            {typeof item === 'object' && item !== null ? (
              <>
                {item.title && <strong>{item.title}</strong>}
                {item.content && <span>{item.content}</span>}
                {!item.title && !item.content && JSON.stringify(item)}
              </>
            ) : (
              item
            )}
          </li>
        ))}
      </ul>
    );
  };

  const renderSection = (section) => {
    if (!section) return null;
    return (
      <div className="mb-4">
        {section.title && <h5 className="font-semibold">{section.title}</h5>}
        {section.content && <p className="text-gray-600">{section.content}</p>}
        {section.items && renderList(section.items)}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {details.title && <h3 className="text-md font-semibold">{details.title}</h3>}
      
      {details.overview && <p>{details.overview}</p>}
      
      {details.bullets && renderList(details.bullets)}
      
      {details.warning && (
        <blockquote className="border-l-4 border-gray-300 pl-4 italic">
          <strong>{details.warning.title}</strong>
          <br />
          {details.warning.content}
        </blockquote>
      )}

      {details.sections && Array.isArray(details.sections) ? (
        details.sections.map((section, index) => (
          <div key={index}>{renderSection(section)}</div>
        ))
      ) : details.sections && typeof details.sections === 'object' ? (
        Object.values(details.sections).map((section, index) => (
          <div key={index}>{renderSection(section)}</div>
        ))
      ) : null}
    </div>
  );
};

// -----------------------------------------------------------------------------
// 4. Main SeparationGuide Component
// -----------------------------------------------------------------------------
const SeparationGuide = () => {
  const { t } = useTranslation(['separation', 'common']);
  const numberOfSteps = 11; // Based on SepLegTimeline steps
  const [completedSteps, setCompletedSteps] = useState(Array(numberOfSteps).fill(false));
  const [historyList, setHistoryList] = useState([]);
  const [activePhase, setActivePhase] = useState('preparation');
  const guideId = "separation";

  // Load progress from localStorage on mount
  useEffect(() => {
    const progress = getGuideProgress(guideId);
    setCompletedSteps(
      Array(numberOfSteps)
        .fill(false)
        .map((_, i) => i < progress)
    );
  }, []);

  // Save progress
  const saveProgress = () => {
    setGuideProgress(guideId, completedSteps.filter(Boolean).length);
    alert(t('common:common.progressSaved'));
  };

  // Mark step as completed
  const markComplete = (index, title) => {
    if (completedSteps[index]) return;
    const newCompleted = [...completedSteps];
    newCompleted[index] = true;
    setCompletedSteps(newCompleted);
    setHistoryList([...historyList, t('common:common.stepCompleted', { title })]);
    setGuideProgress(guideId, newCompleted.filter(Boolean).length);

    // Update active phase based on completed steps
    if (index < 4) setActivePhase('preparation');
    else if (index < 7) setActivePhase('judicial');
    else setActivePhase('postSeparation');
  };

  // Reset all progress
  const resetAll = () => {
    if (window.confirm(t('separationGuide.actions.resetConfirm'))) {
      setCompletedSteps(Array(numberOfSteps).fill(false));
      setHistoryList([]);
      setGuideProgress(guideId, 0);
      setActivePhase('preparation');
    }
  };

  // Calculate progress percentage
  const progressPercentage = Math.round(
    (completedSteps.filter(Boolean).length / numberOfSteps) * 100
  );

  // Define phases with detailed descriptions (from SepLegTimeline)
  const phases = [
    { 
      id: 'preparation', 
      label: t('separationGuide.phases.preparation.title'), 
      color: 'blue',
      description: t('separationGuide.phases.preparation.description'),
      steps: [1, 2, 3, 4]
    },
    { 
      id: 'judicial', 
      label: t('separationGuide.phases.judicial.title'), 
      color: 'amber',
      description: t('separationGuide.phases.judicial.description'),
      steps: [5, 6, 7]
    },
    { 
      id: 'postSeparation', 
      label: t('separationGuide.phases.postSeparation.title'), 
      color: 'green',
      description: t('separationGuide.phases.postSeparation.description'),
      steps: [8, 9, 10, 11]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <DashNavbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
          <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
            <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
            {t('separationGuide.header.title')}
            <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
          </h1>
          <p className="text-center text-blue-600 max-w-3xl mx-auto">
            {t('separationGuide.header.subtitle')}
          </p>
        </div>

        {/* Enhanced Phase Navigation with descriptions */}
        <div className="space-y-4 mb-8">
          <div className="flex justify-between bg-white rounded-lg p-2 shadow-sm">
            {phases.map(phase => (
              <button
                key={phase.id}
                onClick={() => setActivePhase(phase.id)}
                className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                  activePhase === phase.id
                    ? `bg-${phase.color}-100 text-${phase.color}-700`
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {phase.label}
              </button>
            ))}
          </div>
          {/* Phase Description */}
          <div className={`bg-${phases.find(p => p.id === activePhase)?.color}-50 border border-${phases.find(p => p.id === activePhase)?.color}-100 rounded-lg p-4`}>
            <p className="text-sm text-gray-600">
              {phases.find(p => p.id === activePhase)?.description}
            </p>
          </div>
        </div>

        {/* Timeline Overview */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">⏱️</span>
            {t('separationGuide.overview.title')}
          </h3>
          <div className="space-y-4">
            {phases.map(phase => (
              <div key={phase.id} className={`p-4 rounded-lg bg-${phase.color}-50 border border-${phase.color}-100`}>
                <h4 className="font-semibold text-${phase.color}-700 mb-2">{phase.label}</h4>
                <p className="text-sm text-gray-600 mb-2">{phase.description}</p>
                <div className="text-sm text-gray-500">
                  {t('separationGuide.overview.steps')}: {phase.steps.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 3 cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Card 1: Progress */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
              {t('separationGuide.progress.title')}
            </h3>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">{progressPercentage}% {t('separationGuide.progress.completed')}</span>
                <span className="text-slate-500 text-xs">
                  {completedSteps.filter(Boolean).length} / {numberOfSteps} {t('separationGuide.progress.steps')}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Card 2: History */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📝</span>
              {t('separationGuide.actions.lastActions')}
            </h3>
            <div className="h-16 overflow-auto">
              {historyList.length > 0 ? (
                <ul className="text-sm space-y-1">
                  {historyList.slice(-3).map((item, index) => (
                    <li key={index} className="text-gray-600">• {item}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm text-center">
                  {t('separationGuide.progress.noStepsCompleted')}
                </p>
              )}
            </div>
          </div>

          {/* Card 3: Actions */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
              {t('separationGuide.actions.title')}
            </h3>
            <div className="flex justify-center items-center h-16">
              <button
                onClick={resetAll}
                className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
              >
                {t('separationGuide.actions.resetAll')}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Reference Glossary */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">📚</span>
            {t('separationGuide.glossary.title')}
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(t('separationGuide.glossary', { returnObjects: true }))
              .filter(([key]) => key !== 'title')
              .map(([phaseKey, phase]) => (
                <div key={phaseKey}>
                  <h4 className="font-semibold mb-2">{phase.title}</h4>
                  <ul className="text-sm space-y-1">
                    {Object.entries(phase)
                      .filter(([key]) => key !== 'title')
                      .map(([key, value]) => (
                        <li key={key}>{value}</li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>

        {/* Main content */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-blue-100 text-blue-700">
            <h2 className="text-lg font-bold flex items-center">
              <span className="mr-3 text-2xl">📚</span>
              {t('separationGuide.mainContent.title')}
            </h2>
            <p className="text-sm mt-1">
              {t('separationGuide.mainContent.subtitle')}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {[...Array(numberOfSteps)].map((_, index) => {
              const stepNumber = index + 1;
              const phase = phases.find(p => p.steps.includes(stepNumber));
              const stepKey = `step${stepNumber}`;
              
              return (
                <div key={stepKey} className="relative">
                  {phase && (
                    <div className={`absolute -left-2 top-0 px-2 py-1 rounded-md text-xs bg-${phase.color}-100 text-${phase.color}-700`}>
                      {phase.label}
                    </div>
                  )}
                  <Step
                    id={stepKey}
                    title={t(`separationGuide.steps.${stepKey}.title`)}
                    isCompleted={completedSteps[index]}
                    onComplete={() => markComplete(index, t(`separationGuide.steps.${stepKey}.title`))}
                  >
                    {t(`separationGuide.steps.${stepKey}.content`)}
                  </Step>
                </div>
              );
            })}
          </div>
        </div>

        {/* Floating Navigation Menu */}
        <div className="fixed bottom-4 right-4">
          <div className="bg-white rounded-lg shadow-lg p-4">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <span>{t('separationGuide.actions.backToTop')}</span>
            </button>
          </div>
        </div>

        {/* Legal note */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mt-8">
          <p className="italic text-center">
            {t('separationGuide.legalNote')}
          </p>
        </div>
      </main>
    </div>
  );
};

export default SeparationGuide;
